import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import Keycloak from 'keycloak-js'

export type UserRole = 'customer' | 'underwriter' | 'admin' | 'agent'

export interface User {
  id: string
  email: string
  name: string
  firstName: string
  lastName: string
  role: UserRole
  roles: UserRole[]
  customerId: string | null
}

let keycloakInstance: Keycloak | null = null

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const initialized = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isUnderwriter = computed(() => user.value?.roles.includes('underwriter') ?? false)
  const isCustomer = computed(() => user.value?.roles.includes('customer') ?? false)
  const isAdmin = computed(() => user.value?.roles.includes('admin') ?? false)
  const displayName = computed(() => {
    if (user.value) {
      return `${user.value.firstName} ${user.value.lastName}`.trim() || user.value.email
    }
    return 'Guest'
  })

  function extractUser(kc: Keycloak): User {
    const tp = kc.tokenParsed!
    const realmRoles: string[] = tp.realm_access?.roles ?? []

    let primaryRole: UserRole = 'customer'
    if (realmRoles.includes('admin')) primaryRole = 'admin'
    else if (realmRoles.includes('underwriter')) primaryRole = 'underwriter'

    const validRoles: UserRole[] = realmRoles.filter(
      (r): r is UserRole => ['customer', 'underwriter', 'admin', 'agent'].includes(r),
    )

    return {
      id: tp.sub!,
      email: tp.email ?? '',
      name: tp.name ?? `${tp.given_name ?? ''} ${tp.family_name ?? ''}`.trim(),
      firstName: tp.given_name ?? '',
      lastName: tp.family_name ?? '',
      role: primaryRole,
      roles: validRoles,
      customerId: tp.customer_id ?? null,
    }
  }

  function syncState(kc: Keycloak): void {
    if (kc.authenticated && kc.token) {
      token.value = kc.token
      user.value = extractUser(kc)
    } else {
      token.value = null
      user.value = null
    }
  }

  async function init(): Promise<boolean> {
    loading.value = true
    try {
      const kc = new Keycloak({
        url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8180',
        realm: import.meta.env.VITE_KEYCLOAK_REALM || 'warren-ai',
        clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'warren-ai-frontend',
      })

      keycloakInstance = kc

      const authenticated = await kc.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        pkceMethod: 'S256',
        checkLoginIframe: false,
      })

      syncState(kc)

      kc.onTokenExpired = () => {
        kc.updateToken(30)
          .then((refreshed) => {
            if (refreshed) syncState(kc)
          })
          .catch(() => {
            console.warn('[auth] Token refresh failed, logging out')
            logout()
          })
      }

      kc.onAuthSuccess = () => syncState(kc)
      kc.onAuthLogout = () => {
        token.value = null
        user.value = null
      }

      initialized.value = true
      return authenticated
    } catch (err) {
      console.error('[auth] Keycloak init failed:', err)
      initialized.value = true
      return false
    } finally {
      loading.value = false
    }
  }

  async function login(): Promise<void> {
    if (!keycloakInstance) {
      console.error('[auth] Keycloak not initialized')
      return
    }
    await keycloakInstance.login({
      redirectUri: window.location.origin + '/',
    })
  }

  function logout(): void {
    if (!keycloakInstance) return
    keycloakInstance.logout({
      redirectUri: window.location.origin + '/',
    })
  }

  async function getToken(): Promise<string | null> {
    if (!keycloakInstance || !keycloakInstance.authenticated) return null
    try {
      await keycloakInstance.updateToken(5)
      syncState(keycloakInstance)
      return keycloakInstance.token ?? null
    } catch {
      return null
    }
  }

  return {
    user,
    token,
    loading,
    initialized,
    isAuthenticated,
    isUnderwriter,
    isCustomer,
    isAdmin,
    displayName,
    init,
    login,
    logout,
    getToken,
  }
})
